<template>
  <router-view/>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      inactivityTimer: null,
      shopInactivityTimeout: 10 * 60 * 1000, // 사용자: 10분
      adminInactivityTimeout: 60 * 60 * 1000, // 관리자: 1시간
      activityEvents: ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'],
      lastActivityTime: Date.now(),
      throttleDelay: 1000 // 1초마다만 타이머 리셋 (성능 최적화)
    }
  },
  mounted() {
    this.checkSessionOnStartup();
    this.setupInactivityDetection();
  },
  beforeUnmount() {
    this.removeInactivityDetection();
  },
  methods: {
    // 현재 페이지에 맞는 타임아웃 반환
    getCurrentTimeout() {
      const isAdminPage = window.location.pathname.startsWith('/admin');
      return isAdminPage ? this.adminInactivityTimeout : this.shopInactivityTimeout;
    },

    // 앱 시작 시 세션 유효성 검사 (컴퓨터 재시작/브라우저 재접속 대응)
    checkSessionOnStartup() {
      const isAdminPage = window.location.pathname.startsWith('/admin');
      const savedTime = localStorage.getItem('lastActivityTime');

      if (!savedTime) {
        // 활동 시간 기록이 없으면 현재 시간 저장
        localStorage.setItem('lastActivityTime', Date.now().toString());
        return;
      }

      const elapsed = Date.now() - parseInt(savedTime);
      const timeout = this.getCurrentTimeout();

      if (isAdminPage) {
        const adminToken = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
        if (adminToken && elapsed > timeout) {
          console.log('관리자 세션 만료 (비활성 ' + Math.round(elapsed / 60000) + '분)');
          localStorage.removeItem('adminToken');
          sessionStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('lastActivityTime');
          alert('장시간 활동이 없어 자동 로그아웃되었습니다.\n다시 로그인해 주세요.');
          this.$router.push('/admin/login');
          return;
        }
      } else {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        if (token && elapsed > timeout) {
          console.log('사용자 세션 만료 (비활성 ' + Math.round(elapsed / 60000) + '분)');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('token');
          localStorage.removeItem('user_info');
          localStorage.removeItem('user');
          localStorage.removeItem('lastActivityTime');
          alert('장시간 활동이 없어 자동 로그아웃되었습니다.\n다시 로그인해 주세요.');
          this.$router.push('/login');
          return;
        }
      }

      // 세션 유효하면 활동 시간 갱신
      localStorage.setItem('lastActivityTime', Date.now().toString());
    },

    // 일반 사용자 로그인 상태 확인 (관리자 제외)
    isShopUserLoggedIn() {
      // 관리자 토큰이 있으면 제외
      const adminToken = localStorage.getItem('adminToken');
      const adminUser = localStorage.getItem('adminUser');
      if (adminToken || adminUser) {
        return false; // 관리자는 자동 로그아웃 대상 아님
      }

      // 일반 사용자 토큰 확인
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      return !!token;
    },

    // 관리자 로그인 상태 확인
    isAdminLoggedIn() {
      const adminToken = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      return !!adminToken;
    },

    // 비활동 감지 설정
    setupInactivityDetection() {
      // 바인딩된 핸들러 저장 (제거 시 필요)
      this.boundActivityHandler = this.handleUserActivity.bind(this);

      // 활동 이벤트 리스너 추가
      this.activityEvents.forEach(event => {
        document.addEventListener(event, this.boundActivityHandler, { passive: true });
      });

      // 초기 타이머 시작
      this.startInactivityTimer();
    },

    // 비활동 감지 제거
    removeInactivityDetection() {
      // 타이머 클리어
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
        this.inactivityTimer = null;
      }

      // 이벤트 리스너 제거
      if (this.boundActivityHandler) {
        this.activityEvents.forEach(event => {
          document.removeEventListener(event, this.boundActivityHandler);
        });
      }
    },

    // 사용자 활동 핸들러 (쓰로틀링 적용)
    handleUserActivity() {
      const now = Date.now();

      // 쓰로틀링: 마지막 활동 후 1초 이내면 무시
      if (now - this.lastActivityTime < this.throttleDelay) {
        return;
      }

      this.lastActivityTime = now;
      localStorage.setItem('lastActivityTime', now.toString());
      this.startInactivityTimer();
    },

    // 비활동 타이머 시작/리셋
    startInactivityTimer() {
      const isAdminPage = window.location.pathname.startsWith('/admin');

      // 로그인 상태가 아니면 무시
      if (isAdminPage ? !this.isAdminLoggedIn() : !this.isShopUserLoggedIn()) {
        return;
      }

      // 기존 타이머 클리어
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
      }

      // 새 타이머 시작 (사용자: 10분, 관리자: 1시간)
      const timeout = this.getCurrentTimeout();
      this.inactivityTimer = setTimeout(() => {
        this.handleInactivityLogout();
      }, timeout);
    },

    // 비활동으로 인한 자동 로그아웃
    handleInactivityLogout() {
      const isAdminPage = window.location.pathname.startsWith('/admin');

      if (isAdminPage) {
        if (!this.isAdminLoggedIn()) return;

        console.log('1시간 동안 활동이 없어 관리자 자동 로그아웃됩니다.');
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('lastActivityTime');

        alert('장시간 활동이 없어 자동 로그아웃되었습니다.\n다시 로그인해 주세요.');
        this.$router ? this.$router.push('/admin/login') : (window.location.href = '/admin/login');
      } else {
        if (!this.isShopUserLoggedIn()) return;

        console.log('10분간 활동이 없어 자동 로그아웃됩니다.');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
        localStorage.removeItem('user_info');
        localStorage.removeItem('user');
        localStorage.removeItem('lastActivityTime');

        alert('장시간 활동이 없어 자동 로그아웃되었습니다.\n다시 로그인해 주세요.');
        this.$router ? this.$router.push('/login') : (window.location.href = '/login');
      }
    }
  },
  watch: {
    // 라우트 변경 시에도 타이머 리셋 (페이지 이동도 활동으로 간주)
    '$route'() {
      if (this.isShopUserLoggedIn() || this.isAdminLoggedIn()) {
        localStorage.setItem('lastActivityTime', Date.now().toString());
        this.startInactivityTimer();
      }
    }
  }
}
</script>

<style>
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
}
</style>
